import * as React from 'react';
import { getDynamicData } from '@avensia/excite/dynamic-data';
import { type RefObject, useEffect } from 'react';
import { loginEvent, logoutEvent } from '@avensia/excite-commercetools/events';
import { currentUrl, getLocalStorageItem, setLocalStorageItem } from '@avensia/excite';
import { useCheckoutTracking } from './events/checkout';
import { useScrollTracking } from './events/scroll';
import { usePageTracking } from './events/page';
import { useCartTracking } from './events/cart';
import { useErrorTracking } from './events/error';
import { useWishlistTracking } from './events/wishlist';
import { type PageBase, type PageError } from '@avensia/excite';
import { type WishlistUpdateEventType } from '@avensia/excite-commercetools/wishlist';
import { type CartUpdateEventType } from '@avensia/excite-commercetools/cart/command';
import { eventDecorator } from './eventDecorators';
import { type GA4Product } from './utils';
import { ninetailedTrack } from '@avensia/excite-ninetailed';

const USERID_LOCALSTORAGE_KEY = 'nn_user_id';

export function useTracking() {
  useScrollTracking();
  useCheckoutTracking();
  usePageTracking();
  useCartTracking();
  useWishlistTracking();
  useErrorTracking();
  useCustomerTracking();
}

export function useViewEventTrigger<T>(
  ref: RefObject<HTMLElement | null>,
  addToDataLayer: (payload: T) => void,
  payload: T,
  resetTriggerOn: any = null,
) {
  const hasTriggeredRef = React.useRef(false);
  React.useEffect(() => {
    if (ref?.current) {
      const observer = new IntersectionObserver(async ([entry], obs) => {
        if (!entry.isIntersecting) {
          return;
        }

        if (!hasTriggeredRef.current) {
          addToDataLayer(payload);
          hasTriggeredRef.current = true;
        }
        obs.unobserve(entry.target);
      });
      observer.observe(ref?.current);
      return () => observer.disconnect();
    }
  }, [ref, addToDataLayer, payload]);

  useEffect(() => {
    hasTriggeredRef.current = false;
  }, [resetTriggerOn]);

  const resetTrigger = React.useCallback(() => {
    hasTriggeredRef.current = false;
  }, []);

  return [resetTrigger];
}

function useCustomerTracking() {
  useEffect(() => {
    sendCurrentUserDataToDataLayer();

    const customer = getDynamicData<Excite.Commercetools.ICustomerApiModel>();

    customer.on(() => {
      sendCurrentUserDataToDataLayer();
    });

    loginEvent.on(() => {
      addToDataLayer({ event: 'login', ci_level: 0, event_info: { method: 'login' } });
      sendCurrentUserDataToDataLayer();
    });

    logoutEvent.on(() => {
      sendResetUserDataToDataLayer();
    });
  }, []);
}

type CachedUserData = {
  userId: string;
  externalId: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email: string;
  street: string;
  city: string;
  country: string;
  postalCode: string;
  source: 'logged-in' | 'voyado';
  validUntil: number;
  version: 1;
};

function createCachedUserData(
  userId: CachedUserData['userId'],
  externalId: CachedUserData['externalId'],
  firstName: CachedUserData['firstName'],
  lastName: CachedUserData['lastName'],
  phoneNumber: CachedUserData['phoneNumber'],
  email: CachedUserData['email'],
  street: CachedUserData['street'],
  city: CachedUserData['city'],
  country: CachedUserData['country'],
  postalCode: CachedUserData['postalCode'],
  source: CachedUserData['source'],
): CachedUserData {
  return {
    userId,
    externalId,
    firstName,
    lastName,
    phoneNumber,
    street,
    city,
    country,
    postalCode,
    email,
    source,
    validUntil: Date.now() + 1000 * 60 * 60 * 24 * 14,
    version: 1,
  };
}

async function sendCurrentUserDataToDataLayer() {
  const eventInfoDefaults = {
    user_id: 'not_available',
    identified: false,
    logged_in: false,
    member: false,
    session_id: 'not_available',
    client_id: 'not_available',
  };
  const currentUser = await getDynamicData<NordicNest.Api.CustomerApiModel>().current();
  if (currentUser?.isLoggedIn) {
    const cachedData = createCachedUserData(
      currentUser.customerNumber,
      currentUser.externalId,
      currentUser.firstName,
      currentUser.lastName,
      currentUser.mobileNumber,
      currentUser.email,
      currentUser.street,
      currentUser.city,
      currentUser.countryCode,
      currentUser.postalcode,
      'logged-in',
    );
    addToDataLayer({
      event: 'userData',
      userId: currentUser.customerNumber,
      externalId: currentUser.externalId,
      firstName: currentUser.firstName,
      lastName: currentUser.lastName,
      phoneNumber: currentUser.mobileNumber,
      email: currentUser.email,
      ci_level: 0,
      event_info: {
        ...eventInfoDefaults,
        user_id: currentUser.customerNumber,
        identified: true,
        logged_in: true,
        member: currentUser.isMember,
      },
    });
    setLocalStorageItem(USERID_LOCALSTORAGE_KEY, JSON.stringify(cachedData));
    return;
  }

  const storedData = getLocalStorageItem<string>(USERID_LOCALSTORAGE_KEY);
  let cachedData: CachedUserData | undefined;
  if (storedData) {
    try {
      cachedData = JSON.parse(storedData);
      if (cachedData && cachedData.source === 'voyado' && cachedData.validUntil > Date.now()) {
        addToDataLayer({
          event: 'userData',
          userId: cachedData.userId,
          externalId: cachedData.externalId,
          firstName: cachedData.firstName,
          lastName: cachedData.lastName,
          phoneNumber: cachedData.phoneNumber,
          email: cachedData.email,
          ci_level: 0,
          event_info: {
            ...eventInfoDefaults,
            user_id: cachedData.userId,
            identified: true,
          },
        });
        return;
      }
    } catch {
      /* it's either legacy data or malformed data. We can safely ignore it */
    }
  }

  const eclubString = currentUrl().searchParams.get('eclub');
  if (eclubString && eclubString.length > 44) {
    const userData = await getVoyadoUserData(eclubString);
    if (userData?.customerNumber) {
      const cachedData = createCachedUserData(
        userData.customerNumber,
        userData.userId,
        userData.firstName,
        userData.lastName,
        userData.phoneNumber,
        userData.email,
        currentUser.street,
        currentUser.city,
        currentUser.countryCode,
        currentUser.postalcode,
        'voyado',
      );
      addToDataLayer({
        event: 'userData',
        userId: userData.customerNumber,
        externalId: userData.userId,
        ci_level: 0,
        event_info: {
          ...eventInfoDefaults,
          user_id: userData.customerNumber,
          identified: true,
        },
      });
      setLocalStorageItem(USERID_LOCALSTORAGE_KEY, JSON.stringify(cachedData));
    }
    return;
  }

  if (cachedData) {
    addToDataLayer({
      event: 'userData',
      userId: cachedData.userId,
      externalId: cachedData.externalId,
      firstName: cachedData.firstName,
      lastName: cachedData.lastName,
      phoneNumber: cachedData.phoneNumber,
      street: cachedData.street,
      city: cachedData.city,
      country: cachedData.country,
      postalCode: cachedData.postalCode,
      email: cachedData.email,
      ci_level: 0,
      event_info: {
        ...eventInfoDefaults,
        user_id: cachedData.userId,
        identified: !!cachedData.userId,
      },
    });
    return;
  }

  // handling legacy data in local storage
  if (storedData) {
    addToDataLayer({
      event: 'userData',
      userId: storedData,
      event_info: {
        ...eventInfoDefaults,
        user_id: storedData,
      },
    });
  }
}

function sendResetUserDataToDataLayer() {
  addToDataLayer({
    event: 'userDataReset',
    email: null,
    userId: null,
    externalId: null,
    event_info: null,
    firstName: null,
    lastName: null,
    phoneNumber: null,
  });
  localStorage.removeItem(USERID_LOCALSTORAGE_KEY);
}

async function getVoyadoUserData(paramData: string) {
  if (paramData) {
    const userData = await getDynamicData<NordicNest.Api.VoyadoUserDataApiModel>({
      queryData: paramData,
    }).current();

    if (userData) {
      return userData;
    }

    return null;
  }
}

const sendEventsToNinetailed = [
  'page_view',
  'filter_interaction',
  'view_item',
  'select_item',
  'search_result',
  'add_to_wishlist',
];

export function addToDataLayer(action: GA4EcommerceEvent | DataLayerEvent) {
  if (__BROWSER__) {
    if (!window.dataLayer) {
      window.dataLayer = [];
    }
    window.dataLayer.push(eventDecorator(action));
    if (sendEventsToNinetailed.includes(action.event)) {
      const { event, ...rest } = action;
      ninetailedTrack(event, rest);
    }
  }
}

export type DataLayerEvent = Record<string, any> & {
  event: string;
  eventId?: string;
  _clear?: boolean;
};

export type EcommerceAdditionAttributes = { ecommerce?: object; isSponsoredProduct?: boolean; adId?: string };

export type GA4EcommerceEvent = DataLayerEvent & {
  items: Array<GA4Product>;
};

export type EventDecoratorPayload =
  | CartUpdateEventType
  | Excite.Commerce.ITrackingProductInformation
  | Excite.Commercetools.CartApiModel
  | Excite.Commerce.IOrderConfirmationPageApiModel
  | WishlistUpdateEventType
  | PageError
  | PageBase
  | undefined;
